import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { useLocationListing } from '../hooks/useLocationListing';
import { LocationControls, DistanceBadge } from '../components/LocationControls';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import GuestBanner from '../components/GuestBanner';
import GuestAccessibleBadge from '../components/GuestAccessibleBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Calendar as CalendarIcon,
  MapPin,
  Users,
  Plus,
  Loader2,
  Clock,
  Dog,
  Navigation,
  Route,
  ExternalLink,
  CalendarPlus,
  Map,
  List
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import MapView from '../components/MapView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate calendar links for an event
const generateCalendarLinks = (event) => {
  // Parse time safely — handle non-standard values like "Varies"
  let timeStr = event.time || '10:00';
  if (!/\d{1,2}:\d{2}/.test(timeStr)) timeStr = '10:00';
  // Convert "8:00 AM" format to 24h
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  let hours = match ? parseInt(match[1]) : 10;
  const mins = match ? match[2] : '00';
  if (match && match[3]?.toUpperCase() === 'PM' && hours !== 12) hours += 12;
  if (match && match[3]?.toUpperCase() === 'AM' && hours === 12) hours = 0;
  const time24 = `${String(hours).padStart(2, '0')}:${mins}`;
  
  const startDate = new Date(`${event.date}T${time24}`);
  if (isNaN(startDate.getTime())) return { googleLink: '#', icsLink: '#' };
  const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
  
  const formatForGoogle = (date) => date.toISOString().replace(/-|:|\.\d{3}/g, '');
  const formatForICS = (date) => date.toISOString().replace(/-|:|\.\d{3}/g, '').slice(0, -1);
  
  const title = encodeURIComponent(event.title);
  const description = encodeURIComponent(event.description || '');
  const location = encodeURIComponent(event.location || '');
  
  // Google Calendar link
  const googleLink = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatForGoogle(startDate)}/${formatForGoogle(endDate)}&details=${description}&location=${location}`;
  
  // ICS file content for Apple/Outlook
  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${formatForICS(startDate)}Z
DTEND:${formatForICS(endDate)}Z
SUMMARY:${event.title}
DESCRIPTION:${event.description || ''}
LOCATION:${event.location || ''}
END:VEVENT
END:VCALENDAR`;
  
  const icsBlob = new Blob([icsContent], { type: 'text/calendar' });
  const icsLink = URL.createObjectURL(icsBlob);
  
  return { googleLink, icsLink };
};

const Events = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const {
    items: events,
    loading,
    userLocation,
    locationLoading,
    requestLocation,
    clearLocation,
    sortBy,
    setSortBy,
    maxDistance,
    setMaxDistance,
    filters,
    updateFilter,
    refetch
  } = useLocationListing({
    endpoint: '/api/events',
    buildParams: useCallback((filters) => {
      const params = {};
      if (filters.type && filters.type !== 'all') params.event_type = filters.type;
      return params;
    }, []),
    initialFilters: { type: 'all' }
  });
  
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '10:00',
    location: '',
    event_type: 'meetup',
    max_attendees: '',
    image_url: '',
    external_link: '',
    organization_name: ''
  });

  const eventTypes = [
    { value: 'all', label: 'All Events' },
    { value: 'meetup', label: 'Meetups' },
    { value: 'show', label: 'Dog Shows' },
    { value: 'trial', label: 'Agility Trials' },
    { value: 'championship', label: 'Championships' },
    { value: 'expo', label: 'Expos & Conventions' },
    { value: 'workshop', label: 'Workshops' },
    { value: 'walk', label: 'Dog Walks' },
    { value: 'training', label: 'Training Sessions' },
    { value: 'testing', label: 'CGC/Certification Testing' },
    { value: 'adoption', label: 'Adoption Events' }
  ];

  const handleCreateEvent = async () => {
    if (!newEvent.title.trim() || !newEvent.description.trim() || !newEvent.date || !newEvent.location.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreating(true);
    try {
      const eventData = {
        ...newEvent,
        max_attendees: newEvent.max_attendees ? parseInt(newEvent.max_attendees) : null
      };
      const response = await axios.post(`${API}/events`, eventData);
      setEvents([response.data, ...events]);
      setNewEvent({
        title: '',
        description: '',
        date: '',
        time: '10:00',
        location: '',
        event_type: 'meetup',
        max_attendees: '',
        image_url: '',
        external_link: '',
        organization_name: ''
      });
      setSelectedDate(null);
      setCreateOpen(false);
      toast.success('Event created!');
      refetch();
    } catch (error) {
      toast.error('Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const handleJoinEvent = async (eventId, e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/events/${eventId}/join`);
      setEvents(events.map(ev => 
        ev.id === eventId ? { ...ev, attendees_count: ev.attendees_count + 1 } : ev
      ));
      toast.success('Joined event!');
    } catch (error) {
      if (error.response?.data?.detail === 'Already joined') {
        toast.info('You\'ve already joined this event');
      } else {
        toast.error('Failed to join event');
      }
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      'meetup': 'bg-primary/10 text-primary',
      'workshop': 'bg-secondary/10 text-secondary',
      'walk': 'bg-accent text-accent-foreground',
      'training': 'bg-info/10 text-info',
      'adoption': 'bg-warning/10 text-warning',
      'show': 'bg-amber-100 text-amber-800',
      'competition': 'bg-blue-100 text-blue-800',
      'expo': 'bg-purple-100 text-purple-800',
      'festival': 'bg-green-100 text-green-800'
    };
    return colors[type] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Events"
        description="Find dog-friendly events near you. Meetups, training sessions, adoption events, and more from the AADC community."
        url="/events"
      />
      <div className="container mx-auto px-4 py-8">
        {/* Guest Banner */}
        <GuestBanner message="Sign up to create events, RSVP, and connect with other pet owners" />
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-4xl font-heading font-medium">Events & Meetups</h1>
              <GuestAccessibleBadge />
            </div>
            <p className="text-muted-foreground text-lg">
              Connect with dog lovers at local events and training sessions
            </p>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <LocationControls
              userLocation={userLocation}
              locationLoading={locationLoading}
              onRequestLocation={requestLocation}
              onClearLocation={clearLocation}
              maxDistance={maxDistance}
              onMaxDistanceChange={setMaxDistance}
              sortBy={sortBy}
              onSortByChange={setSortBy}
            />
            {/* Map/List Toggle */}
            <div className="flex rounded-lg border overflow-hidden">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode('list')}
                data-testid="events-list-view-btn"
              >
                <List className="w-4 h-4 mr-1" />
                List
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode('map')}
                data-testid="events-map-view-btn"
              >
                <Map className="w-4 h-4 mr-1" />
                Map
              </Button>
            </div>
            {user && (
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="create-event-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Event
                  </Button>
                </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Create an Event</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Event Title *</Label>
                    <Input
                      placeholder="e.g., Monthly Dog Walk"
                      value={newEvent.title}
                      onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                      data-testid="event-title-input"
                      className="input-field"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea
                      placeholder="Tell people what to expect..."
                      value={newEvent.description}
                      onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                      rows={3}
                      data-testid="event-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                            data-testid="event-date-picker"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(date) => {
                              setSelectedDate(date);
                              setNewEvent({ ...newEvent, date: date ? format(date, 'yyyy-MM-dd') : '' });
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>Time *</Label>
                      <Input
                        type="time"
                        value={newEvent.time}
                        onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                        className="input-field"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Location *</Label>
                    <Input
                      placeholder="Address or venue name"
                      value={newEvent.location}
                      onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                      data-testid="event-location-input"
                      className="input-field"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Event Type</Label>
                      <Select
                        value={newEvent.event_type}
                        onValueChange={(val) => setNewEvent({ ...newEvent, event_type: val })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="meetup">Meetup</SelectItem>
                          <SelectItem value="show">Dog Show</SelectItem>
                          <SelectItem value="trial">Agility Trial</SelectItem>
                          <SelectItem value="championship">Championship</SelectItem>
                          <SelectItem value="expo">Expo/Convention</SelectItem>
                          <SelectItem value="workshop">Workshop</SelectItem>
                          <SelectItem value="walk">Dog Walk</SelectItem>
                          <SelectItem value="training">Training Session</SelectItem>
                          <SelectItem value="testing">CGC/Testing</SelectItem>
                          <SelectItem value="adoption">Adoption Event</SelectItem>
                          <SelectItem value="competition">Competition</SelectItem>
                          <SelectItem value="festival">Festival</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Max Attendees</Label>
                      <Input
                        type="number"
                        placeholder="Unlimited"
                        value={newEvent.max_attendees}
                        onChange={(e) => setNewEvent({ ...newEvent, max_attendees: e.target.value })}
                        className="input-field"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Organization/Club Name</Label>
                    <Input
                      placeholder="e.g., AKC, Westminster Kennel Club"
                      value={newEvent.organization_name}
                      onChange={(e) => setNewEvent({ ...newEvent, organization_name: e.target.value })}
                      className="input-field"
                    />
                    <p className="text-xs text-muted-foreground">Who is hosting this event?</p>
                  </div>

                  <div className="space-y-2">
                    <Label>External Link</Label>
                    <Input
                      placeholder="https://official-event-page.com"
                      value={newEvent.external_link}
                      onChange={(e) => setNewEvent({ ...newEvent, external_link: e.target.value })}
                      className="input-field"
                    />
                    <p className="text-xs text-muted-foreground">Link to official event page for more info</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Cover Image URL</Label>
                    <Input
                      placeholder="https://..."
                      value={newEvent.image_url}
                      onChange={(e) => setNewEvent({ ...newEvent, image_url: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <Button 
                    className="w-full btn-primary"
                    onClick={handleCreateEvent}
                    disabled={creating}
                    data-testid="submit-event-btn"
                  >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Event
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        {/* Filter by Event Type */}
        <div className="mb-6">
          <Select value={filters.type} onValueChange={(v) => updateFilter('type', v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              {eventTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Events Grid/Map */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : events.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <CalendarIcon className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="font-heading text-xl mb-2">No events found</h3>
              <p className="text-muted-foreground mb-4">
                {filters.type !== 'all' ? 'Try a different filter' : 'Be the first to create an event!'}
              </p>
              {user && (
                <Button className="btn-primary" onClick={() => setCreateOpen(true)}>
                  Create First Event
                </Button>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'map' ? (
          /* Map View */
          <MapView
            height="550px"
            dataTestId="events-map-view"
            markers={events.map(event => ({
              id: event.id,
              lat: event.latitude,
              lng: event.longitude,
              title: event.title,
              color: event.event_type === 'show' ? '#d97706' : event.event_type === 'competition' ? '#2563eb' : event.event_type === 'expo' ? '#7c3aed' : event.event_type === 'festival' ? '#16a34a' : '#c9762b',
              event,
            }))}
            renderPopup={(marker) => (
              <div className="min-w-[200px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getTypeColor(marker.event.event_type)}`}>
                    {marker.event.event_type}
                  </span>
                </div>
                <h3 className="font-bold text-sm mb-1">{marker.event.title}</h3>
                <p className="text-xs text-gray-500 mb-1">{marker.event.date} &middot; {marker.event.time}</p>
                <p className="text-xs text-gray-500 mb-1">{marker.event.location}</p>
                {(marker.event.external_link || marker.event.external_url) && (
                  <a
                    href={marker.event.external_link || marker.event.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Official Page &rarr;
                  </a>
                )}
              </div>
            )}
          />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <Card 
                key={event.id}
                className="card-interactive overflow-hidden"
                data-testid={`event-card-${event.id}`}
              >
                <div className="aspect-video bg-muted relative overflow-hidden">
                  {event.image_url ? (
                    <img 
                      src={event.image_url}
                      alt={event.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20">
                      <Dog className="w-12 h-12 text-primary/40" />
                    </div>
                  )}
                  <Badge className={`absolute top-3 left-3 ${getTypeColor(event.event_type)}`}>
                    {event.event_type}
                  </Badge>
                  
                  {/* Date badge */}
                  <div className="event-date-badge absolute top-3 right-3">
                    <div className="day">{new Date(event.date).getDate()}</div>
                    <div className="month">{format(new Date(event.date), 'MMM')}</div>
                  </div>
                </div>
                
                <CardContent className="p-4">
                  <h3 className="font-heading text-lg font-medium mb-2 line-clamp-1">{event.title}</h3>
                  <p className="text-muted-foreground text-sm line-clamp-2 mb-4">{event.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>{event.time}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate">{event.location}</span>
                      </div>
                      {event.distance_miles !== null && event.distance_miles !== undefined && (
                        <Badge variant="secondary" className="flex items-center gap-1 text-xs ml-2">
                          <Route className="w-3 h-3" />
                          {event.distance_miles} mi
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="w-4 h-4" />
                      <span>
                        {event.attendees_count} attending
                        {event.max_attendees && ` / ${event.max_attendees} max`}
                      </span>
                    </div>
                    {(event.external_link || event.external_url) && (
                      <a 
                        href={event.external_link || event.external_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4" />
                        Official Event Page
                      </a>
                    )}
                    {event.source && (
                      <div className="text-xs text-muted-foreground" data-testid={`event-source-${event.id}`}>
                        Source: {event.source_url ? (
                          <a href={event.source_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                            {event.source}
                          </a>
                        ) : event.source}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {user && (
                      <Button 
                        className="flex-1"
                        variant="outline"
                        onClick={(e) => handleJoinEvent(event.id, e)}
                        data-testid={`join-event-${event.id}`}
                      >
                        Join Event
                      </Button>
                    )}
                    
                    {/* Add to Calendar Dropdown */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="icon" title="Add to Calendar">
                          <CalendarPlus className="w-4 h-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-48 p-2" align="end">
                        <div className="space-y-1">
                          <a
                            href={generateCalendarLinks(event).googleLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors w-full"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <CalendarIcon className="w-4 h-4" />
                            Google Calendar
                          </a>
                          <a
                            href={generateCalendarLinks(event).icsLink}
                            download={`${event.title.replace(/\s+/g, '_')}.ics`}
                            className="flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors w-full"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <CalendarIcon className="w-4 h-4" />
                            Apple/Outlook (.ics)
                          </a>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;
